/**
 * router/index.ts
 *
 * Manual routes configuration
 */

import { createRouter, createWebHistory } from 'vue-router'

import Default from '@/layouts/Default.vue'
import LoginUser from '@/views/Authentication/LoginUser.vue'
import AuthCallback from '@/views/Authentication/AuthCallback.vue'

import { useAppStore } from '@/stores/app'
import { useAppCookies } from '@/composables/useCookies'
import { routeNames } from '@/constants/router'
import { authService, isEmbedded } from '@/api/client'
import { TokenHelper } from '@deltax/idp-api-client'
import AppService from '@/services/appService'
import { authCallbackGuard, globalRouterGuard, loginGuard } from '@/router/authGuards'

import { useDoMoreActionsStore } from '@/stores/doMoreActions'
import { useCanvasStore } from '@/stores/canvas'
import { useInputStore } from '@/stores/input'
import { useTranslateStore } from '@/stores/translate'
import { useGenerateStore } from '@/stores/generate'
import { useDynamicCreativeStore } from '@/stores/dynamicCreative'
import { handleEmbedFlowError, requestSessionTokenFromParent } from '@/helpers/embedHelper'

const createAppRouter = () => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
      {
        path: '/login',
        name: routeNames.LOGIN,
        component: LoginUser,
        meta: { requiresAuth: false },
        beforeEnter: loginGuard,
      },
      {
        path: '/callback',
        name: routeNames.CALLBACK,
        component: AuthCallback,
        meta: { requiresAuth: false },
      },
      {
        path: '/',
        name: routeNames.DEFAULT,
        component: Default,
        children: [
          {
            path: '/auth',
            name: routeNames.AUTHENTICATION,
            component: () => import('@/views/Authentication/Authentication.vue'),
            meta: { requiresAuth: true },
            children: [
              {
                path: '/profileSelection',
                name: routeNames.PROFILE_SELECTION,
                component: () => import('@/views/Authentication/ProfileSelection.vue'),
                meta: {
                  title: 'Profile Selection',
                  requiresAuth: true,
                },
              },
            ],
          },
          {
            path: 'app/:businessProfileId?',
            name: routeNames.APP,
            component: () => import('@/layouts/HomePage.vue'),
            meta: { requiresAuth: true },
            beforeEnter: async (to, from, next) => {
              const businessProfileId = Number(to.params.businessProfileId)
              if (isNaN(businessProfileId) || businessProfileId <= 0) {
                next({ name: routeNames.NOT_FOUND })
                return
              }
              if (to.matched[to.matched.length - 1].name === routeNames.APP) {
                next({ name: routeNames.MODE, params: { businessProfileId } })
                return
              }
              const appStore = useAppStore()
              if (!appStore.user) {
                await appStore.hydrateUserFromToken()
              }
              if (appStore.portal == null) {
                const portalDomain = import.meta.env.DEV ? 'uni.stunnr.co' : window.location.hostname
                try {
                  const appService = new AppService()
                  appStore.setPortal(await appService.getPortalData(portalDomain))
                } catch (error) {
                  console.error(error)
                }
              }
              if (appStore.businessProfiles.length === 0 && appStore.user && appStore.portal) {
                const appService = new AppService()
                try {
                  appStore.setBusinessProfiles(
                    await appService.getPortalBusinessProfiles(
                      appStore.user.id,
                      appStore.portal.id,
                    ),
                  )
                } catch (error) {
                  console.error(error)
                }
              }

              const hasAccess = appStore.businessProfiles.some((bp) => bp.id === businessProfileId)
              if (!hasAccess && appStore.businessProfiles.length > 0) {
                next({ name: routeNames.NOT_FOUND })
                return
              }

              appStore.setSelectedBusinessProfileId(Number(to.params.businessProfileId))
              next()
            },
            children: [
              {
                path: 'mode',
                name: routeNames.MODE,
                component: () => import('@/views/LandingPage.vue'),
                meta: { requiresAuth: true },
              },
              {
                path: 'resize',
                name: routeNames.RESIZE,
                component: () => import('@/components/Resize.vue'),
                meta: { requiresAuth: true },
              },
              {
                path: 'editor',
                name: routeNames.EDITOR,
                component: () => import('@/views/Editor.vue'),
                meta: { requiresAuth: true },
              },
              {
                path: 'generate',
                name: routeNames.GENERATE,
                component: () => import('@/components/GenerateCreative.vue'),
                meta: { requiresAuth: true },
              },
              {
                path: 'translate',
                name: routeNames.TRANSLATE,
                component: () => import('@/views/Translate.vue'),
                meta: { requiresAuth: true },
              },
              {
                path: 'icon',
                name: routeNames.BUTTON_GIF,
                component: () => import('@/views/ButtonGif.vue'),
                meta: { requiresAuth: true },
              },
              {
                path: 'generate-similar',
                name: routeNames.DYNAMIC_CREATIVE,
                component: () => import('@/views/DynamicCreative.vue'),
                meta: { requiresAuth: true },
              },
              {
                path: 'ai-image-edit',
                name: routeNames.AI_IMAGE_EDIT,
                component: () => import('@/views/AIEditor.vue'),
                meta: { requiresAuth: true },
              },
              {
                path: 'ai-edit-inputs',
                name: routeNames.AI_IMAGE_EDIT_INPUTS,
                component: () => import('@/components/AIEditorInputs.vue'),
                meta: { requiresAuth: true },
              },
              {
                path: 'icon-inputs',
                name: routeNames.BUTTON_GIF_INPUTS,
                component: () => import('@/views/ButtonGifInputs.vue'),
                meta: { requiresAuth: true },
              },
              {
                path: 'grab-engine',
                name: routeNames.GRAB_ENGINE,
                component: () => import('@/components/grab-resized-creatives/GrabEngineContainer.vue'),
                meta: { requiresAuth: true },
              },
            ],
          },
          {
            path: '/not-found',
            name: routeNames.NOT_FOUND,
            component: () => import('@/views/Error/PageNotFound.vue'),
          },
          {
            path: '/forbidden',
            name: 'Forbidden',
            component: () => import('@/views/Error/PageForbidden.vue'),
          },
        ],
      },
      {
        path: '/:pathMatch(.*)*',
        redirect: { name: routeNames.NOT_FOUND },
      },
    ],
  })

  router.beforeEach(async (to, from, next) => {
    const appStore = useAppStore()

    if (isEmbedded) {
      appStore.setIsInEmbedMode(true)
    }

    const currentToken = authService.getAccessToken()
    const isTokenMissingOrExpired = !currentToken || TokenHelper.isExpired(currentToken)
    const hasRefreshToken = Boolean((authService as any)._refreshToken)

    if (
      isEmbedded &&
      (isTokenMissingOrExpired || !hasRefreshToken) &&
      to.name !== routeNames.CALLBACK &&
      to.name !== routeNames.LOGIN
    ) {
      try {
        const { getIsSessionRevoked } = await import('@/helpers/embedHelper')
        if (getIsSessionRevoked()) {
          next(false)
          return
        }

        const businessProfileId = Number(to.params.businessProfileId) || appStore.selectedBusinessProfileId
        if (!businessProfileId || businessProfileId <= 0) {
          const { default: InvalidBusinessProfileRequestedError } = await import('@/errors/InvalidBusinessProfileRequestedError')
          throw new InvalidBusinessProfileRequestedError('Business Profile ID is required in embed mode')
        }
        appStore.setSelectedBusinessProfileId(businessProfileId)

        sessionStorage.setItem('dx_internal_redirect', to.fullPath)

        const sessionToken = await requestSessionTokenFromParent()

        const authorizeUrl = await authService.createAuthorizationUrl(
          `${window.location.origin}/callback`,
          undefined,
          import.meta.env.VITE_RESOURCE,
          sessionToken
        )

        window.onbeforeunload = null
        window.location.href = authorizeUrl
        next(false) // Cancel Vue navigation since we are redirecting the window
        return
      } catch (error) {
        handleEmbedFlowError(error)
        appStore.setIsInEmbedMode(false)
        next(false)
        return
      }
    }

    const fromRoutes = [routeNames.TRANSLATE, routeNames.RESIZE]
    const toRoutes = [
      routeNames.TRANSLATE,
      routeNames.GENERATE,
      routeNames.DYNAMIC_CREATIVE,
    ]

    if (
      from.name &&
      to.name &&
      fromRoutes.includes(from.name.toString()) &&
      toRoutes.includes(to.name.toString())
    ) {
      const doMoreActions = useDoMoreActionsStore()
      const action = doMoreActions.popActionResult()

      if (action) {
        const data = action.data
        const canvasStore = useCanvasStore()
        const inputStore = useInputStore()

        if (data.fabricCanvas) {
          canvasStore.setFabricCanvas(data.fabricCanvas)
        }
        if (data.canvasJson) {
          canvasStore.setCanvasJson(data.canvasJson)
        }
        if (data.inputUrl) {
          inputStore.setInputS3Url(data.inputUrl)
        }

        if (action.type === 'Translate') {
          useTranslateStore().$patch(data.state)
        } else if (action.type === 'Generate') {
          useGenerateStore().$patch(data.state)
        } else if (action.type === 'DynamicCreative') {
          useDynamicCreativeStore().$patch(data.state)
        }
      }
    }

    if (to.name === routeNames.LOGIN || to.name === routeNames.CALLBACK) {
      next()
      return
    }

    await globalRouterGuard(to, from, next)
  })

  return router
}

export default createAppRouter
